'use client';

import type { ReactNode } from 'react';

import type { ButtonProps } from '../../ui';
import type { IconName } from '../icons';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { isValidElement } from 'react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../ui';
import { Icon } from '../icons';

// POLY-1733 — orange dot flagging the operation currently in progress on the
// contract. Uses the semantic warning token, no bespoke colour. The dot itself
// is decorative; an sr-only label exposes the status to assistive tech.
const InProgressDot = ({ label }: { label?: string }) => (
  <>
    <span
      aria-hidden
      className="size-2 shrink-0 rounded-full bg-semantic-warning-foreground"
      data-testid="menu-item-in-progress-dot"
    />
    {!!label && <span className="sr-only">{label}</span>}
  </>
);

// POLY-1733 — destructive items (e.g. "Archiver") stay red in every state and
// get a light-red background on hover/focus. Destructive design-system token.
const destructiveItemClassName =
  'text-destructive focus:bg-destructive/10 focus:text-destructive';

const MenuLeafItem = ({
  disabled,
  icon,
  inProgress,
  inProgressLabel,
  label,
  onClick,
  testId,
  variant,
}: Pick<
  MenuItem,
  | 'disabled'
  | 'icon'
  | 'inProgress'
  | 'inProgressLabel'
  | 'label'
  | 'onClick'
  | 'testId'
  | 'variant'
>) => (
  <DropdownMenuItem
    className={cn(variant === 'destructive' && destructiveItemClassName)}
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
  >
    {!!icon && <Icon name={icon} size="sm" />}
    {label}
    {!!inProgress && <InProgressDot label={inProgressLabel} />}
  </DropdownMenuItem>
);

const SubMenuWithElement = ({
  label,
  subItem,
}: Pick<MenuItem, 'label' | 'subItem'>) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger>{label}</DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="min-w-fit">
      {subItem}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

const SubMenuWithItems = ({
  disabled,
  inProgress,
  inProgressLabel,
  label,
  subItems,
  testId,
}: Pick<
  MenuItem,
  | 'disabled'
  | 'inProgress'
  | 'inProgressLabel'
  | 'label'
  | 'subItems'
  | 'testId'
>) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger data-testid={testId} disabled={disabled}>
      {label}
      {!!inProgress && <InProgressDot label={inProgressLabel} />}
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="min-w-fit">
      {subItems?.map((subItem, subIndex) => (
        <div key={subIndex}>
          {!!subItem.separator && <DropdownMenuSeparator />}
          {subItem.subItems && subItem.subItems.length > 0 ? (
            <SubMenuWithItems
              disabled={subItem.disabled}
              inProgress={subItem.inProgress}
              inProgressLabel={subItem.inProgressLabel}
              label={subItem.label}
              subItems={subItem.subItems}
              testId={subItem.testId}
            />
          ) : (
            <MenuLeafItem
              disabled={subItem.disabled}
              icon={subItem.icon}
              inProgress={subItem.inProgress}
              inProgressLabel={subItem.inProgressLabel}
              label={subItem.label}
              onClick={subItem.onClick}
              testId={subItem.testId}
              variant={subItem.variant}
            />
          )}
        </div>
      ))}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

type MenuItem = {
  disabled?: boolean;
  icon?: IconName;
  // POLY-1733 — render an orange "operation in progress" dot after the label.
  inProgress?: boolean;
  // POLY-1733 — sr-only text describing the in-progress dot for assistive tech.
  inProgressLabel?: string;
  label: string;
  onClick: () => void;
  // POLY-1733 — section header rendered above this item (e.g. "Opérations").
  sectionLabel?: string;
  separator?: boolean;
  bottomSeparator?: boolean;
  subItem?: ReactNode;
  subItems?: MenuItem[];
  testId?: string;
  // POLY-1733 — "destructive" turns the label + icon red (e.g. "Archiver").
  variant?: 'default' | 'destructive';
};

export type ActionDropdownProps = {
  align?: 'center' | 'end' | 'start';
  className?: string;
  items: MenuItem[];
  variant?: ButtonProps['variant'];
} & (
  | {
      isOpen: boolean;
      onOpenChange: (isOpen: boolean) => void;
    }
  | {
      isOpen?: never;
      onOpenChange?: never;
    }
);

export const ActionDropdown = ({
  align = 'start',
  className,
  items,
  isOpen,
  onOpenChange,
  variant = 'outline',
}: ActionDropdownProps) =>
  !!items.length && (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          isIcon
          aria-label="Actions"
          className={cn(className)}
          data-testid="action-dropdown-trigger"
          variant={variant}
        >
          <Icon className="rotate-90" name="ellipsis" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {items.map((item, index) => (
          <div key={index}>
            {!!item.separator && <DropdownMenuSeparator />}
            {!!item.sectionLabel && (
              <DropdownMenuLabel>{item.sectionLabel}</DropdownMenuLabel>
            )}
            {!!item.subItem && isValidElement(item.subItem) && (
              <SubMenuWithElement label={item.label} subItem={item.subItem} />
            )}
            {!item.subItem && !!item.subItems && item.subItems.length > 0 && (
              <SubMenuWithItems
                disabled={item.disabled}
                inProgress={item.inProgress}
                inProgressLabel={item.inProgressLabel}
                label={item.label}
                subItems={item.subItems}
                testId={item.testId}
              />
            )}
            {!item.subItem &&
              (!item.subItems || item.subItems.length === 0) && (
                <MenuLeafItem
                  disabled={item.disabled}
                  icon={item.icon}
                  inProgress={item.inProgress}
                  inProgressLabel={item.inProgressLabel}
                  label={item.label}
                  onClick={item.onClick}
                  testId={item.testId}
                  variant={item.variant}
                />
              )}
            {!!item.bottomSeparator && <DropdownMenuSeparator />}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
